import { useEffect, type ReactNode } from 'react';
import { create } from 'zustand';

import { readThemeCookie, serialiseThemeCookie } from './cookie';
import { DEFAULT_THEME, isTheme, THEME_STORAGE_KEY, type Theme } from './tokens';

/**
 * The attribute is already on `<html>` before this module is parsed — the
 * inline bootstrap in `index.html` stamps it ahead of the stylesheet so the
 * theme never flashes. This provider only keeps React's copy in step with the
 * document and owns the write path for the toggle.
 *
 * The raw string is stored, not a serialised store, because the pre-paint
 * script reads the same key and cannot afford to parse a wrapper.
 */

function readStoredTheme(): Theme {
  const attribute = document.documentElement.getAttribute('data-theme');
  if (isTheme(attribute)) return attribute;

  // The cookie before storage: it is the only one of the two a sibling origin
  // could have written, so it is the newer whenever it exists.
  const carried = readThemeCookie();
  if (carried) return carried;

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isTheme(stored)) return stored;
  } catch {
    // A blocked storage partition is not a reason to fail the render.
  }

  return DEFAULT_THEME;
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: DEFAULT_THEME,
  setTheme: (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    // The cookie is what carries the choice to the public site and the
    // subscriber dashboard; localStorage reaches neither.
    try {
      document.cookie = serialiseThemeCookie(
        theme,
        window.location.hostname,
        window.location.protocol === 'https:',
      );
    } catch {
      // A refused cookie leaves the choice local to this origin.
    }
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Persisting is a convenience; rendering is not.
    }
    set({ theme });
  },
  toggleTheme: () => get().setTheme(get().theme === 'dark' ? 'light' : 'dark'),
}));

export function ThemeProvider({ children }: { children: ReactNode }): ReactNode {
  useEffect(() => {
    const current = readStoredTheme();
    if (useThemeStore.getState().theme !== current) {
      useThemeStore.setState({ theme: current });
    }
  }, []);

  return children;
}
